import { useState } from 'react';
import type {
  BoardroomRanking,
  BoardroomSearchMarket,
  BoardroomSearchTerm,
  BoardroomSearchTermHit,
} from '../../../types';
import SerpModal from '../../SerpModal';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { VolumeBar } from './Sparkline';
import DataTable from './DataTable';
import type { Column } from './tableColumns';
import { EngineTag, EnginesNote, engineCounts, engineLabel } from './engines';

/**
 * The search market: every term the room's demand lookups returned, searched a
 * batch per dig, and who is on page one for each.
 *
 * Clicks are ESTIMATES — monthly volume × the share of clicks a position
 * usually gets — and are labelled as such everywhere. Added up per company they
 * give its share of search: the closest thing the room has to how much of the
 * market's attention each player actually gets.
 *
 * EVERY NUMBER OPENS ITS TERMS, AND EVERY TERM OPENS ITS SEARCH. A count of
 * keywords (Terms, Top 3) opens a row underneath listing them; a keyword,
 * wherever it appears, opens the SERP viewer on what that search found.
 */

const n = (v: number | null | undefined) => (v === null || v === undefined ? '-' : v.toLocaleString());

function growth(v: number | null | undefined) {
  if (v === null || v === undefined) return <span className="text-gray-400">-</span>;
  const cls =
    v > 5 ? 'text-emerald-700 dark:text-emerald-300' : v < -5 ? 'text-rose-700 dark:text-rose-300' : 'text-gray-500';
  return <span className={cls}>{`${v > 0 ? '+' : ''}${Math.round(v)}%`}</span>;
}

function Share({ pct }: { pct: number }) {
  return (
    <div className="flex items-center gap-1.5" title={`${pct}% of estimated clicks`}>
      <div className="h-1.5 w-16 rounded-full bg-gray-100 dark:bg-gray-700">
        <div className="h-1.5 rounded-full bg-indigo-400 dark:bg-indigo-500" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
      </div>
      <span className="tabular-nums text-gray-600 dark:text-gray-300">{pct}%</span>
    </div>
  );
}

const STATUS_WORDS: Record<BoardroomSearchTerm['status'], string> = {
  searched: '',
  pending: 'searching…',
  failed: 'search failed',
  waiting: 'next dig',
};

const section = 'border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3';
const h3 = 'text-[11px] uppercase tracking-wide text-gray-400 mb-1';
const th = 'py-1 font-normal';
const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline';

// ----------------------------- the generic pieces -----------------------------

/** A search to open in the SERP viewer. */
export interface SerpTarget {
  id: number;
  keyword: string;
}

/** A keyword that opens its search. Plain text until it has been searched. */
export function SerpLink({
  keyword,
  keywordId,
  onSerp,
  className = '',
}: {
  keyword: string;
  keywordId: number | null | undefined;
  onSerp: (t: SerpTarget) => void;
  className?: string;
}) {
  if (!keywordId) return <span className={className}>{keyword}</span>;
  return (
    <button
      type="button"
      onClick={() => onSerp({ id: keywordId, keyword })}
      title="Open what this search found"
      className={`${LINK} text-left ${className}`}
      data-testid={`serp-link-${keywordId}`}
    >
      {keyword}
    </button>
  );
}

/** A count that opens the list it counts. */
export function CountButton({
  value,
  open,
  onClick,
  title,
  testId,
}: {
  value: number;
  open: boolean;
  onClick: () => void;
  title: string;
  testId: string;
}) {
  if (!value) return <span className="text-gray-400">0</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      title={title}
      className={`tabular-nums ${open ? 'font-semibold text-indigo-700 dark:text-indigo-300' : LINK}`}
      data-testid={testId}
    >
      {value}
    </button>
  );
}

/** The terms a count stands for, each opening its search. */
export function TermList({
  terms,
  onSerp,
  heading,
}: {
  terms: BoardroomSearchTermHit[];
  onSerp: (t: SerpTarget) => void;
  heading: string;
}) {
  return (
    <div className="py-1 pl-2" data-testid="term-list">
      <div className="mb-1 text-[11px] uppercase tracking-wide text-gray-400">{heading}</div>
      <table className="text-xs">
        <thead className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          <tr>
            <th className={`${th} text-left pr-4`}>Term</th>
            <th className={`${th} text-right pr-4`}>Position</th>
            <th className={`${th} text-right pr-4`}>Searches/mo</th>
            <th className={`${th} text-right`}>Clicks/mo</th>
          </tr>
        </thead>
        <tbody>
          {terms.map(t => (
            <tr key={t.keyword} className="border-t border-gray-100 dark:border-gray-700/60">
              <td className="py-0.5 pr-4">
                <SerpLink keyword={t.keyword} keywordId={t.keyword_id} onSerp={onSerp} />
              </td>
              <td className="py-0.5 pr-4 text-right tabular-nums">#{t.position}</td>
              <td className="py-0.5 pr-4 text-right tabular-nums">{n(t.volume)}</td>
              <td className="py-0.5 text-right tabular-nums">{n(t.clicks)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Which row's list is open, and which list: every term, or the top 3. */
export type Opened = { key: string; what: 'all' | 'top3' } | null;

export function useOpened() {
  const [opened, setOpened] = useState<Opened>(null);
  const toggle = (key: string, what: 'all' | 'top3') =>
    setOpened(o => (o && o.key === key && o.what === what ? null : { key, what }));
  const close = () => setOpened(null);
  return { opened, toggle, close };
}

// ----------------------------- the report -----------------------------

/** What the lens kept and left out, in one line. Pure. */
export function lensNote(l: NonNullable<BoardroomSearchMarket['lens']>): string {
  const off = l.off_board
    ? ` ${l.off_board.toLocaleString()} site${l.off_board === 1 ? '' : 's'} that ${l.off_board === 1 ? 'is' : 'are'} not a company here ${l.off_board === 1 ? 'is' : 'are'} hidden too: a tag can only be on a company.`
    : '';
  return `Narrowed by the lens: ${l.companies.toLocaleString()} of ${l.companies_all.toLocaleString()} companies, and the searches where one of them is on page one.${off} Shares are of all page-one clicks.`;
}

type Player = BoardroomSearchMarket['players'][number];
type Site = BoardroomSearchMarket['unclaimed'][number];

export default function SearchMarketReport({
  data,
  onOpen,
  roomName,
}: {
  data: BoardroomSearchMarket;
  onOpen: (itemId: number) => void;
  roomName?: string;
}) {
  const [serp, setSerp] = useState<SerpTarget | null>(null);
  const players = useOpened();
  const sites = useOpened();
  // ESCAPE FOLDS AN OPEN LIST BACK UP. Not while the SERP viewer is open: it
  // closes on Escape too, and one press should close one thing — the viewer
  // first, then the list behind it.
  useEscapeKey(!serp && Boolean(players.opened || sites.opened), () => {
    players.close();
    sites.close();
  });

  const t = data.totals;
  if (!t.keywords) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">
        No keywords yet. The pile grows from the demand analyst’s lookups - every one adds its terms and the
        related terms Google offers - and each dig searches the next batch.
      </div>
    );
  }
  const maxVol = Math.max(0, ...data.rows.map(r => r.volume ?? 0));

  /** The Terms and Top 3 columns, for any row that carries its terms. */
  function countColumns<R extends { terms: BoardroomSearchTermHit[]; keywords: number }>(
    state: ReturnType<typeof useOpened>,
    keyOf: (r: R) => string,
    withTop3: boolean,
  ): Column<R>[] {
    const isOpen = (r: R, what: 'all' | 'top3') =>
      state.opened?.key === keyOf(r) && state.opened.what === what;
    const cols: Column<R>[] = [
      {
        key: 'keywords',
        label: 'Terms',
        width: 6,
        align: 'right',
        value: r => r.keywords,
        cell: r => (
          <CountButton
            value={r.keywords}
            open={isOpen(r, 'all')}
            onClick={() => state.toggle(keyOf(r), 'all')}
            title="The terms it is on page one for"
            testId={`terms-${keyOf(r)}`}
          />
        ),
      },
    ];
    if (withTop3) {
      cols.push({
        key: 'top3',
        label: 'Top 3',
        width: 6,
        align: 'right',
        value: r => r.terms.filter(x => x.position <= 3).length,
        cell: r => (
          <CountButton
            value={r.terms.filter(x => x.position <= 3).length}
            open={isOpen(r, 'top3')}
            onClick={() => state.toggle(keyOf(r), 'top3')}
            title="The terms it holds position 1–3 for"
            testId={`top3-${keyOf(r)}`}
          />
        ),
      });
    }
    return cols;
  }

  function detailFor<R extends { terms: BoardroomSearchTermHit[] }>(
    state: ReturnType<typeof useOpened>,
    keyOf: (r: R) => string,
    name: (r: R) => string,
  ) {
    return {
      expanded: (r: R) => state.opened?.key === keyOf(r),
      detail: (r: R) => {
        const top3 = state.opened?.what === 'top3';
        const terms = top3 ? r.terms.filter(x => x.position <= 3) : r.terms;
        return (
          <TermList
            terms={terms}
            onSerp={setSerp}
            heading={`${name(r)} · ${top3 ? 'top 3 for' : 'page one for'} ${terms.length} term${terms.length === 1 ? '' : 's'}`}
          />
        );
      },
    };
  }

  const playerKey = (p: Player) => `player-${p.company_id}`;
  const siteKey = (u: Site) => `site-${u.domain}`;

  // SORTABLE WHERE A NUMBER OR A NAME CAN BE ORDERED; the lists of who is on
  // page one are content, not something to sort by.
  const playerCols: Column<Player>[] = [
    {
      key: 'company',
      label: 'Company',
      width: 18,
      always: true,
      value: p => p.company,
      cell: p => (
        <>
          <button type="button" onClick={() => onOpen(p.company_id)} className={`${LINK} text-left`}>
            {p.company}
          </button>
          <div className="text-[11px] text-gray-400">{p.domain}</div>
        </>
      ),
    },
    { key: 'share', label: 'Share of search', width: 12, value: p => p.share, cell: p => <Share pct={p.share} /> },
    { key: 'clicks', label: 'Clicks/mo', width: 8, align: 'right', value: p => p.clicks, cell: p => n(p.clicks) },
    ...countColumns<Player>(players, playerKey, true),
    { key: 'best', label: 'Best', width: 6, align: 'right', value: p => p.best },
    {
      key: 'terms',
      label: 'Ranks for',
      width: 30,
      sortable: false,
      value: p => p.terms.map(x => `${x.keyword} (#${x.position})`).join(' · '),
      cell: p => (
        <span className="line-clamp-2 text-gray-500 dark:text-gray-400">
          {p.terms.slice(0, 8).map((x, i) => (
            <span key={x.keyword}>
              {i > 0 && ' · '}
              <SerpLink keyword={x.keyword} keywordId={x.keyword_id} onSerp={setSerp} /> (#{x.position})
            </span>
          ))}
        </span>
      ),
    },
  ];

  const siteCols: Column<Site>[] = [
    {
      key: 'domain',
      label: 'Site',
      width: 18,
      always: true,
      value: u => u.domain,
      cell: u => (
        <a href={`https://${u.domain}`} target="_blank" rel="noopener noreferrer" className="text-gray-900 dark:text-gray-100 hover:underline">
          {u.domain}
        </a>
      ),
    },
    { key: 'share', label: 'Share of search', width: 12, value: u => u.share, cell: u => <Share pct={u.share} /> },
    { key: 'clicks', label: 'Clicks/mo', width: 8, align: 'right', value: u => u.clicks, cell: u => n(u.clicks) },
    ...countColumns<Site>(sites, siteKey, true),
    { key: 'best', label: 'Best', width: 6, align: 'right', value: u => u.best },
  ];

  const termCols: Column<BoardroomSearchTerm>[] = [
    {
      key: 'keyword',
      label: 'Term',
      width: 18,
      always: true,
      value: r => r.keyword,
      cell: r => (
        <>
          <SerpLink
            keyword={r.keyword}
            keywordId={r.status === 'searched' ? r.keyword_id : null}
            onSerp={setSerp}
            className={r.source === 'related' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'}
          />
          {r.off_market && (
            <span
              className="ml-1 text-[11px] text-gray-400"
              title="Searched, and no company in this room came up: the name means something else here. Not counted in the totals."
              data-testid={`off-market-${r.keyword}`}
            >
              · means something else
            </span>
          )}
        </>
      ),
    },
    {
      key: 'engine',
      label: 'Engine',
      width: 7,
      value: r => r.engine,
      cell: r => (r.engine ? <span title={engineLabel(r.engine)}>{r.engine}</span> : <span className="text-gray-400">-</span>),
    },
    {
      key: 'volume',
      label: 'Searches/mo',
      width: 10,
      align: 'right',
      value: r => r.volume,
      cell: r => (
        <span className="whitespace-nowrap">
          <VolumeBar value={r.volume} max={maxVol} width={40} /> {n(r.volume)}
        </span>
      ),
    },
    { key: 'growth', label: 'Growth', width: 6, align: 'right', value: r => r.growth_pct, cell: r => growth(r.growth_pct) },
    {
      key: 'top_bid',
      label: 'Top bid',
      width: 6,
      align: 'right',
      value: r => r.top_bid,
      cell: r => (r.top_bid !== null && r.top_bid !== undefined ? `$${r.top_bid}` : '-'),
    },
    { key: 'competition', label: 'Competition', width: 7, value: r => r.competition },
    { key: 'source', label: 'Source', width: 6, value: r => r.source },
    { key: 'on_board', label: 'On board', width: 6, align: 'right', value: r => (r.status === 'searched' ? r.on_board : null) },
    {
      key: 'top',
      label: 'Page one',
      width: 30,
      sortable: false,
      value: r =>
        r.status === 'searched'
          ? r.top.slice(0, 5).map(h => `${h.position}. ${h.company ?? h.domain}`).join(' · ')
          : STATUS_WORDS[r.status],
      cell: r =>
        r.status !== 'searched' ? (
          <span className="italic text-gray-400">{STATUS_WORDS[r.status]}</span>
        ) : (
          <span className="line-clamp-2 text-gray-500 dark:text-gray-400">
            {r.top.slice(0, 5).map((hit, i) => (
              <span key={`${hit.position}-${hit.domain}`}>
                {i > 0 && ' · '}
                {hit.position}.{' '}
                {hit.company_id ? (
                  <button type="button" onClick={() => onOpen(hit.company_id as number)} className={LINK}>
                    {hit.company}
                  </button>
                ) : (
                  hit.domain
                )}
              </span>
            ))}
            {r.keyword_id && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => setSerp({ id: r.keyword_id as number, keyword: r.keyword })}
                  className={`${LINK} whitespace-nowrap`}
                  title="Every result for this search"
                >
                  all results →
                </button>
              </>
            )}
          </span>
        ),
    },
  ];

  return (
    <div className="min-w-0 space-y-3 text-xs">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t.keywords.toLocaleString()} terms on the pile · {t.searched.toLocaleString()} searched
        {t.waiting ? ` · ${t.waiting.toLocaleString()} waiting for the next dig` : ''} ·{' '}
        {t.volume.toLocaleString()} searches a month · about {t.clicks.toLocaleString()} clicks a month on page one
        of the terms searched. Clicks are estimates: volume × the usual share for each position. Click a count to
        see its terms, and a term to see its search.
      </p>
      <EnginesNote counts={engineCounts(data.rows.filter(r => r.status === 'searched'))} />
      {data.lens && <p className="text-xs text-indigo-700 dark:text-indigo-300" data-testid="search-market-lens">{lensNote(data.lens)}</p>}

      {data.players.length > 0 && (
        <section className={section} data-testid="search-market-players">
          <DataTable
            table="search_market_players"
            room={roomName}
            file="search-market-players"
            title={<h3 className={h3}>Who holds the searches</h3>}
            columns={playerCols}
            rows={data.players}
            rowKey={playerKey}
            {...detailFor<Player>(players, playerKey, p => p.company)}
          />
        </section>
      )}

      {data.unclaimed.length > 0 && (
        <section className={section} data-testid="search-market-unclaimed">
          <DataTable
            table="search_market_sites"
            room={roomName}
            file="search-market-sites"
            title={<h3 className={h3}>On page one, not on the board</h3>}
            beforeTable={
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Sites taking clicks on these terms that are not a company here - publishers, marketplaces, and players
                nobody has found yet. The miner works through them as leads.
              </p>
            }
            columns={siteCols}
            rows={data.unclaimed}
            rowKey={siteKey}
            {...detailFor<Site>(sites, siteKey, u => u.domain)}
          />
        </section>
      )}

      <section className={section} data-testid="search-market-terms">
        <DataTable
          table="search_market_terms"
          room={roomName}
          file="search-market-terms"
          title={<h3 className={h3}>The terms</h3>}
          columns={termCols}
          rows={data.rows}
          rowKey={r => `term-${r.keyword}`}
        />
      </section>

      {serp && (
        <SerpModal isOpen onClose={() => setSerp(null)} keywordId={serp.id} keywordValue={serp.keyword} />
      )}
    </div>
  );
}

/** The terms one company or product ranks for, on its profile. Each term
 *  opens its search. */
export function RankingsTable({ rankings }: { rankings: BoardroomRanking[] }) {
  const [serp, setSerp] = useState<SerpTarget | null>(null);
  const clicks = rankings.reduce((a, r) => a + (r.clicks || 0), 0);
  return (
    <div className="text-xs">
      <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
        On page one for {rankings.length} term{rankings.length === 1 ? '' : 's'} · about {clicks.toLocaleString()} clicks
        a month (estimated).
      </p>
      <table className="w-full">
        <thead className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          <tr>
            <th className={`${th} text-left`}>Term</th>
            <th className={`${th} text-right`}>Position</th>
            <th className={`${th} text-right`}>Searches/mo</th>
            <th className={`${th} text-right`}>Growth</th>
            <th className={`${th} text-right`}>Clicks/mo</th>
            <th className={`${th} text-left pl-3`}>Page</th>
          </tr>
        </thead>
        <tbody>
          {rankings.map(r => (
            <tr key={r.keyword} className="border-t border-gray-100 dark:border-gray-700/60">
              <td className="py-1 pr-2">
                <SerpLink keyword={r.keyword} keywordId={r.keyword_id} onSerp={setSerp} />
              </td>
              <td className="py-1 pr-2 text-right tabular-nums whitespace-nowrap">
                #{r.position}
                <EngineTag engine={r.engine} />
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">{n(r.volume)}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{growth(r.growth_pct)}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{n(r.clicks)}</td>
              <td className="py-1 pl-3 text-gray-500 dark:text-gray-400 truncate max-w-[16rem]">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    {r.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                ) : (
                  '-'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {serp && <SerpModal isOpen onClose={() => setSerp(null)} keywordId={serp.id} keywordValue={serp.keyword} />}
    </div>
  );
}
